// OrgViewBanner.js — persistent "you're inside a client's workspace" banner.
// Modeled on ImpersonationBanner (approved Option B, 2026-10-01): the org
// console's workspace entry rides the same state machinery admin
// impersonation uses — explicit audited ENTER (POST /api/orgs/enter-trust)
// and EXIT (POST /api/orgs/enter-trust/{id}/log-exit), a fixed top banner
// that is impossible to miss, and one-tap return to the Org Console.
// Server-side scoping is unchanged: every trust data endpoint keeps
// enforcing require_org_grant (level, expiry, revocation).
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, X, UserCog } from 'lucide-react';
import { toast } from 'sonner';
import { fetchWithAuth } from '@/utils/api';

const BACKEND = process.env.REACT_APP_BACKEND_URL || 'https://api.trustoffice.app';

const fmtExpiry = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch { return ''; }
};

export const OrgViewBanner = () => {
  const navigate = useNavigate();
  const [exiting, setExiting] = useState(false);
  const [viewTick, setViewTick] = useState(0);
  let view = null;
  try { view = JSON.parse(sessionStorage.getItem('org_view_data') || 'null'); } catch { view = null; }

  // Keep the banner reactive + push page content down while viewing
  // (same body-class contract ImpersonationBanner uses).
  useEffect(() => {
    // Re-run on every viewTick (event fires on enter + exit): apply the
    // push-down while viewing, remove it when the view ends.
    if (view) {
      document.body.classList.add('impersonating');
    } else {
      document.body.classList.remove('impersonating');
    }
    const onStorage = () => setViewTick((t) => t + 1);
    window.addEventListener('org_view_changed', onStorage);
    return () => {
      if (!view) document.body.classList.remove('impersonating');
      window.removeEventListener('org_view_changed', onStorage);
    };
  }, [viewTick]);  // view re-read each tick
  if (!view) return null;

  const handleExit = async () => {
    setExiting(true);
    try {
      // Best-effort audit; the exit itself must always succeed.
      try {
        await fetchWithAuth(`${BACKEND}/api/orgs/enter-trust/${view.trust_id}/log-exit`, {
          method: 'POST',
        });
      } catch (e) {
        console.error('Failed to log org workspace exit:', e);
      }
      sessionStorage.removeItem('org_view_data');
      window.dispatchEvent(new Event('org_view_changed'));
      // 2026-10-12 (demo-battery defect D1): the client trust was still the
      // sticky active-trust selection after exit — localStorage kept it "valid"
      // via the isOrgViewTrust reconcile only while org_view_data existed, so
      // Money pages could render the CLIENT's data inside the advisor's own
      // workspace until a hard reload. Clear the selection with the view and
      // ask AuthContext to re-reconcile (it self-heals to the advisor's own
      // first real trust once org_view_data is gone).
      localStorage.removeItem('selected_trust_id');
      window.dispatchEvent(new Event('trust_selection_invalidated'));
      toast.success('Returned to Org Console');
      navigate('/org-console');
    } finally {
      setExiting(false);
    }
  };

  const expiry = view.expires_at ? ` · access ends ${fmtExpiry(view.expires_at)}` : '';

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[100] bg-violet-700 text-white px-4 py-2 shadow-lg"
      data-testid="org-view-banner"
      role="status"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex items-center gap-2 bg-violet-800 rounded-full px-3 py-1 shrink-0">
            <Eye className="w-4 h-4" />
            <span className="text-sm font-medium">VIEWING</span>
          </div>
          <div className="flex items-center gap-2 min-w-0">
            <UserCog className="w-5 h-5 shrink-0" />
            {/* R3 (council 2026-10-09): the CLIENT is the headline; the trust is context.
                Subline makes menu-conformity explicit: every menu below is the client's. */}
            <span className="font-semibold truncate">
              {view.client_name || view.trust_name || "Client's account"}
            </span>
            <span className="hidden md:inline whitespace-nowrap text-violet-200 text-xs">
              {view.trust_name ? `· ${view.trust_name}` : ''} · {view.view_level}{expiry}
            </span>
          </div>
          <div className="hidden lg:block text-[11px] text-violet-200 truncate">
            You're viewing {view.client_name || 'the client'}'s account — all menus below reflect their workspace. Actions are logged and attributed to you.
          </div>
        </div>
        <button
          onClick={handleExit}
          disabled={exiting}
          data-testid="org-view-exit"
          className="flex items-center gap-2 bg-white text-violet-800 rounded-full px-4 py-1.5 text-sm font-semibold hover:bg-violet-50 transition-colors shrink-0 focus:outline-none focus:ring-2 focus:ring-white"
        >
          <X className="w-4 h-4" />
          {exiting ? 'Returning…' : 'Back to Org Console'}
        </button>
      </div>
    </div>
  );
};

export default OrgViewBanner;